import type { Message } from "@ag-ui/client";
import type { Agent } from "~/services/agents";
import type { ProjectConversation } from "~/services/projects";

// The composer's active agent for a conversation's next turn, persisted in
// sessionStorage: it survives a refresh and clears when the tab closes.
const nextTurnAgentStorageKey = (conversationId: string) => `next-turn-agent:${conversationId}`;

export const readNextTurnAgent = (conversationId: string): string | null => {
  try {
    return sessionStorage.getItem(nextTurnAgentStorageKey(conversationId));
  } catch {
    return null;
  }
};

export const writeNextTurnAgent = (conversationId: string, agentId: string) => {
  try {
    sessionStorage.setItem(nextTurnAgentStorageKey(conversationId), agentId);
  } catch {
    // Storage unavailable (e.g. private browsing) — the selection just won't
    // survive a refresh.
  }
};

// A turn's opening user message, as /connect serves it — names the turn's
// agent via `agentId` when the turn has one.
function namesTurnAgent(message: Message): message is Message & { agentId: string } {
  return message.role === "user" && "agentId" in message && typeof message.agentId === "string";
}

// The agent of the latest turn that names one — a turn without an agent is
// skipped, matching how the server derives the conversation's current agent.
export function latestTurnAgentId(messages: readonly Message[]): string | null {
  return messages.findLast(namesTurnAgent)?.agentId ?? null;
}

// The project's default agent: the latest session's agent if still usable,
// else the first usable agent. Null when none is usable.
export function defaultSessionAgentId(
  usableAgents: readonly Pick<Agent, "id">[],
  sessions: readonly Pick<ProjectConversation, "agent">[]
): string | null {
  const lastUsedAgentId = sessions.find((s) => s.agent?.id)?.agent?.id;
  return usableAgents.find((a) => a.id === lastUsedAgentId)?.id ?? usableAgents[0]?.id ?? null;
}
